import React from "react";
import { statusColors } from "../../../orders/OrderSidePanal";
import { ThemeBasedColorHeader } from "src/shared/modules/profile/theme/themeCheacker";
import { useDateTimeFormatter } from "src/shared/configurations/hooks/useDateTimeFormatter";

const PAYMENT_METHODS = ["Cash", "Card"];
const isPayment = (id?: string) => PAYMENT_METHODS.includes(id || "");

const GRID = "grid grid-cols-[3%_11%_8%_8%_12%_9%_10%_9%_9%_10%_10%]";

export type OrderItem = {
  Orderdate: string;
  Ordertime: string;
  orderid?: string;
  Total_price?: number;
  ReceivedAmount?: number;
  discount?: number;
  refundAmount?: number;
  RemainingAmount?: number;
  CurrentTotalRemaining?: number;
  order_status?: string;
  billNumber?: string;
  paymentSource?: string;
  adjustAmount?: number;
};

// ✅ Backend sort key — matches your API field names
export type SortKey =
  | "orderid"
  | "billNumber"
  | "order_status"
  | "orderedOn"
  | "Total_price"
  | "ReceivedAmount"
  | "CurrentTotalRemaining"
  | "discount"
  | "deliveryFee"
  | "orderAmount"
  | "adjustAmount";

export type SortDir = "asc" | "desc";

type Props = {
  orders?: OrderItem[];
  selectedOrderId: string;
  onRowSelect: (orderAmount: number, orderId: string) => void;
  onNavigate: (orderId: string) => void;
  sortKey: SortKey | null;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
};

const COLS: { label: string; key: SortKey | null; right?: boolean }[] = [
  { label: "#", key: null },
  { label: "Date", key: "orderedOn" },
  { label: "Order ID", key: "orderid" },
  { label: "Bill No", key: "billNumber" },
  { label: "Status", key: "order_status" },
  { label: "Order (QR)", key: "orderAmount", right: true },
  { label: "Payment (QR)", key: "ReceivedAmount", right: true },
  { label: "Refund (QR)", key: null, right: true },
  { label: "Adjust (QR)", key: null, right: true },
  { label: "Balance (QR)", key: null, right: true },
  { label: "OSB (QR)", key: "CurrentTotalRemaining", right: true },
];

function fmt(n?: number) {
  return n != null && n > 0 ? n.toFixed(2) : "—";
}

function OrderTable({ orders = [], selectedOrderId, onRowSelect, onNavigate, sortKey, sortDir, onSort }: Props) {
  const { formatDate, formatTime } = useDateTimeFormatter();
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100">
        <span className="text-sm font-bold text-slate-800">Order history</span>
        <span className="ml-2 text-xs text-slate-500">
          {orders.length} {orders.length === 1 ? "order" : "orders"}
        </span>
      </div>

      {/* Table Head */}
      <div className={`${GRID} px-4 py-2 ${ThemeBasedColorHeader()} border-b border-slate-200 sticky top-0 z-10`}>
        {COLS.map(({ label, key, right }) => {
          const active = sortKey === key;
          const icon = !key ? "" : active ? (sortDir === "asc" ? "▲" : "▼") : "⇅";
          return (
            <div
              key={label}
              onClick={() => key && onSort(key)}
              className={`flex items-center gap-1 text-[11px] uppercase tracking-wider ${right ? "justify-end" : ""} ${key ? "cursor-pointer hover:text-slate-900" : ""}`}
            >
              {label}
              {key && <span className={`text-[9px] ${active ? "text-blue-500" : "opacity-40"}`}>{icon}</span>}
            </div>
          );
        })}
      </div>

      {/* Body */}
      <div className="overflow-y-auto flex-1">
        {orders.map((item, i) => {
          const amt = item.Total_price ?? 0;
          const pay = item.ReceivedAmount ?? 0;
          const ref = Math.abs(item.refundAmount ?? 0);
          const bal = amt - pay - ref;
          const osb = item.CurrentTotalRemaining ?? 0;
          const isPure = isPayment(item.orderid) && amt === 0;
          const status = item.order_status ?? "Unknown";
          const isSelected = item.orderid === selectedOrderId;
          const clickable = !isPure && amt > 0;
          const adjust = item.adjustAmount ?? 0;

          return (
            <div
              key={i}
              onClick={() => clickable && onRowSelect(amt, item.orderid || "")}
              className={[
                GRID,
                "px-4 py-2.5 border-b text-[13px] items-center transition",
                isSelected ? "bg-blue-50" : i % 2 === 0 ? "bg-white hover:bg-slate-50" : "bg-slate-50/50 hover:bg-slate-100",
                clickable ? "cursor-pointer" : "cursor-default",
              ].join(" ")}
            >
              <div className="text-slate-400 text-xs">{i + 1}</div>
              <div>
                <div className="text-[12px] text-slate-700">{formatDate(item.Orderdate)}</div>
                <div className="text-[11px] text-slate-400">{formatTime(item.Ordertime)}</div>
              </div>
              <div className="min-w-0 overflow-hidden pr-2">
                {item.orderid && !isPayment(item.orderid) ? (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigate(item.orderid!);
                    }}
                    className="truncate block text-blue-600 underline cursor-pointer"
                  >
                    {item.orderid}
                  </span>
                ) : (
                  <span className="truncate block text-slate-500">{item.paymentSource || "—"}</span>
                )}
              </div>
              <div className="text-slate-500 text-[12px]">{item.billNumber || "—"}</div>
              <div>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${statusColors[status] || "bg-gray-200 text-gray-700"}`}>{status}</span>
              </div>
              <div className="text-right text-blue-700 font-semibold">{fmt(amt)}</div>
              <div className="text-right text-green-600 font-semibold">{fmt(pay)}</div>
              <div className="text-right text-amber-600 font-semibold">{fmt(ref)}</div>
              <div className="text-right text-amber-900 font-semibold">{fmt(adjust)}</div>
              <div className={`text-right font-semibold ${isPure ? "text-slate-400" : bal > 0 ? "text-red-600" : "text-green-600"}`}>{isPure ? "—" : Math.abs(bal).toFixed(2)}</div>
              <div className="text-right text-violet-700 font-bold">{osb.toFixed(2)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default OrderTable;
